import React, { useEffect, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { updatePayment, formatINR } from '../../features/payments/memberPayments'

const toForm = e => ({
  MemberName: e?.name || '',
  PaymentType: e?.type || 'Additional area',
  AreaSqft: e ? String(e.areaSqft) : '',
  AreaCost: e ? String(e.areaCost) : '',
  Installment1: e ? String(e.installment1) : '',
  Installment2: e ? String(e.installment2) : '',
  Installment3: e ? String(e.installment3) : '',
  Installment4: e ? String(e.installment4) : '',
  TotalReceived: e ? String(e.totalReceived) : ''
})

const MONEY = [
  ['Installment1', 'Instalment 1'],
  ['Installment2', 'Instalment 2'],
  ['Installment3', 'Instalment 3'],
  ['Installment4', 'Instalment 4']
]

const n = v => (v === '' || v == null ? NaN : Number(v))

// Admin dialog to edit one payment. Totals / outstanding / advance are
// recalculated (here as a preview, and again on the server).
const Editpaymentmodal = ({ show, entry, onClose, onSaved }) => {
  const [form, setForm] = useState(toForm(entry))
  const [errors, setErrors] = useState({})
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (show) {
      setForm(toForm(entry))
      setErrors({})
    }
  }, [show, entry])

  const set = field => e => {
    const value = e.target.value
    setForm(prev => ({ ...prev, [field]: value }))
    setErrors(prev => ({ ...prev, [field]: undefined }))
  }

  const payable = MONEY.reduce((s, [k]) => s + (n(form[k]) || 0), 0)
  const received = n(form.TotalReceived) || 0
  const outstanding = Math.max(0, payable - received)
  const advance = Math.max(0, received - payable)

  const validate = () => {
    const e = {}
    if (!form.MemberName.trim()) e.MemberName = 'Name is required'
    ;['AreaSqft', 'AreaCost', 'Installment1', 'Installment2', 'Installment3', 'Installment4', 'TotalReceived'].forEach(k => {
      const v = n(form[k])
      if (Number.isNaN(v) || v < 0) e[k] = 'Enter a number, 0 or more'
    })
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const submit = async ev => {
    ev.preventDefault()
    if (!entry || !validate()) return
    setSaving(true)
    try {
      const payload = {
        MemberName: form.MemberName.trim(),
        PaymentType: form.PaymentType,
        AreaSqft: n(form.AreaSqft),
        AreaCost: n(form.AreaCost),
        Installment1: n(form.Installment1),
        Installment2: n(form.Installment2),
        Installment3: n(form.Installment3),
        Installment4: n(form.Installment4),
        TotalReceived: n(form.TotalReceived)
      }
      const saved = await updatePayment(entry.id, payload)
      toast.success('Payment updated')
      onSaved(saved)
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update payment')
    } finally {
      setSaving(false)
    }
  }

  const field = (name, label, input, hint) => (
    <div className="mb-3">
      <label className="form-label" htmlFor={`edit-pay-${name}`}>{label}</label>
      {input}
      {hint && <div className="form-text">{hint}</div>}
      {errors[name] && <div className="invalid-feedback d-block">{errors[name]}</div>}
    </div>
  )

  const numInput = (name, step = '0.01') => (
    <input
      id={`edit-pay-${name}`}
      type="number"
      inputMode="decimal"
      min="0"
      step={step}
      className={`form-control${errors[name] ? ' is-invalid' : ''}`}
      value={form[name]}
      onChange={set(name)}
    />
  )

  return (
    <Modal show={show} onHide={saving ? () => {} : onClose} centered scrollable backdrop="static" aria-labelledby="edit-payment-title">
      <form onSubmit={submit} noValidate>
        <Modal.Header closeButton closeLabel="Close">
          <Modal.Title as="h2" id="edit-payment-title" className="h3 mb-0">
            Edit payment{entry ? ` · ${entry.flatNo}` : ''}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {field('MemberName', 'Member name',
            <input id="edit-pay-MemberName" className={`form-control${errors.MemberName ? ' is-invalid' : ''}`} value={form.MemberName} onChange={set('MemberName')} />
          )}

          <div className="row g-3">
            <div className="col-sm-6">
              {field('PaymentType', 'Type',
                <select id="edit-pay-PaymentType" className="form-select" value={form.PaymentType} onChange={set('PaymentType')}>
                  <option>Additional area</option>
                  <option>Additional flat</option>
                </select>
              )}
            </div>
            <div className="col-sm-6">
              {field('AreaSqft', 'Area (sq.ft)', numInput('AreaSqft'))}
            </div>
          </div>

          {field('AreaCost', 'Cost of area (₹)', numInput('AreaCost'))}

          <div className="row g-3">
            {MONEY.map(([k, label]) => (
              <div className="col-sm-6" key={k}>{field(k, `${label} (₹)`, numInput(k))}</div>
            ))}
          </div>

          {field('TotalReceived', 'Total received (₹)', numInput('TotalReceived'), 'Everything the society has received for this entry.')}

          <div className="bl-edit-preview" aria-live="polite">
            <div><span>Total payable</span><strong>{formatINR(payable)}</strong></div>
            <div><span>Outstanding</span><strong className={outstanding >= 1 ? 'text-danger' : 'text-success'}>{formatINR(outstanding)}</strong></div>
            <div><span>Advance</span><strong>{formatINR(advance)}</strong></div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving && <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>}
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  )
}

export default Editpaymentmodal
